import React, { memo } from 'react'
import {
  TextField,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Grid
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { biannuals, quarters, months, annuals } from '../../../../../../../../assets/js/variables'
import { KeyboardDatePicker } from '@material-ui/pickers'
import moment, { isMoment } from 'moment'
import Button from '../../../../../../../UI/Button/Button'

const detailPanelColumns = {
  result_kpi_id: { label: '' },
  result_item_id: { label: '' },
  frequency_no: { label: '' },
  target_value: { label: 'targetValue' },
  target_range1: { key: 1, label: '1', style: { color: 'white', backgroundColor: 'red' } },
  target_range2: { key: 2, label: '2', style: { color: 'white', backgroundColor: 'orange' } },
  target_range3: { key: 3, label: '3', style: { color: 'navy', backgroundColor: 'yellow' } },
  target_range4: { key: 4, label: '4', style: { color: 'white', backgroundColor: 'green' } },
  target_range5: { key: 5, label: '5', style: { color: 'white', backgroundColor: 'darkgreen' } },
  actual_value: { label: 'actualValue' }
}

export const DetailPanelHeader = memo(({ translate }) => {
  const columns = Object.entries(detailPanelColumns).map(listItem => {
    let [listItemKey] = listItem

    if (['result_kpi_id', 'result_item_id', 'actual_value', 'is_na'].includes(listItemKey)) {
      return null
    }

    return (
      <TableCell key={`${listItemKey}DetailHeader`} style={detailPanelColumns[listItemKey]?.style}>
        {translate(detailPanelColumns[listItemKey].label) ||
          detailPanelColumns[listItemKey]?.label ||
          ''}
      </TableCell>
    )
  })
  return (
    <TableHead>
      <TableRow>{columns}</TableRow>
    </TableHead>
  )
})

export const DetailPanelBody = ({
  detailList,
  rowChanged,
  frequencyCode = 'FQT01',
  kpiTypeDate = false,
  translate,
  targetScore,
  adopted
}) => {
  let rowNames = ['Annual']
  switch (frequencyCode) {
    case 'FQT02':
      rowNames = biannuals.map(item => translate(item.stringVar))
      break
    case 'FQT03':
      rowNames = quarters.map(item => translate(item.stringVar))
      break
    case 'FQT04':
      rowNames = months.map(item => translate(item.stringVar))
      break
    case 'FQT01':
    default:
      rowNames = annuals.map(item => translate(item.stringVar))
  }

  const detailDisplay = Array.from(rowNames, (rowName, index) => {
    let row
    try {
      row = detailList.find(row => row.frequency_no === index + 1)
    } catch (e) {
      row = null
    }
    if (!row) {
      row = {
        result_kpi_id: '',
        result_item_id: '',
        frequency_no: index + 1,
        target_value: '',
        target_range1: '',
        target_range2: '',
        target_range3: '',
        target_range4: '',
        target_range5: '',
        actual_value: ''
      }
    }

    const dataColumns = Object.entries(row).map(column => {
      let [columnKey, columnValue] = column
      let inputElement = null
      const inputKey = detailPanelColumns[columnKey]?.key || 0
      const inputDisabled = inputKey > targetScore

      if (kpiTypeDate) {
        columnValue = columnValue ? moment(columnValue, 'YYYY-MM-DD') : null

        inputElement = (
          <KeyboardDatePicker
            disabled={adopted || inputDisabled}
            margin='normal'
            format='YYYY MM DD'
            value={columnValue}
            onChange={date =>
              rowChanged({ ...row, [columnKey]: isMoment(date) ? date.format('YYYY-MM-DD') : date })
            }
            KeyboardButtonProps={{
              'aria-label': 'change date'
            }}
          />
        )
      } else {
        inputElement = (
          <TextField
            disabled={adopted || inputDisabled}
            type={'number'}
            value={isNaN(parseFloat(columnValue)) ? '' : columnValue}
            onChange={e => rowChanged({ ...row, [columnKey]: e.target.value })}
            onBlur={() =>
              rowChanged({
                ...row,
                [columnKey]: isNaN(parseFloat(columnValue))
                  ? ''
                  : parseFloat(columnValue).toFixed(4)
              })
            }
          />
        )
      }

      if (['result_kpi_id', 'result_item_id', 'actual_value', 'is_na'].includes(columnKey)) {
        return null
      }
      if (columnKey === 'frequency_no') {
        inputElement = rowName
      }
      return <TableCell key={columnKey}>{inputElement}</TableCell>
    })
    return <TableRow key={index}>{dataColumns}</TableRow>
  })
  return <TableBody>{detailDisplay}</TableBody>
}

export const DetailPanelAction = memo(({ applyButtonClicked, translate }) => {
  return (
    <TableHead>
      <TableRow>
        <TableCell colSpan={6}></TableCell>
        <TableCell>
          <Button color={'primary'} clicked={applyButtonClicked}>
            {translate('applyAll')}
          </Button>
        </TableCell>
      </TableRow>
    </TableHead>
  )
})

export const DetailPanelFooter = ({
  responsibleSearchChanged,
  loadingQueue,
  rowData,
  inputChanged,
  translate,
  appraisalType,
  adopted
}) => {
  const loading = loadingQueue.includes('employee')
  const filterValue = rowData.responsible_filter

  let responsible = rowData?.responsible ?? null
  if (rowData.adopted) {
    responsible = rowData.adopt_item?.responsible ?? responsible
  }

  let selected = null
  let responsibleInput = null

  const responsibleOptions = (rowData.responsible_list || []).map(item => {
    const option = {
      key: +item.emp_detail_id,
      value: +item.emp_detail_id,
      label: `${item.full_name} (${item.org_abbr}), (${item.position_name})`
    }
    if (+rowData.responsible_id === +item.emp_detail_id && !filterValue) {
      selected = option
    }
    return option
  })

  if (responsible && !filterValue) {
    selected = {
      key: +responsible.emp_detail_id,
      value: +responsible.emp_detail_id,
      label: `${responsible.full_name} (${responsible.org_abbr}), (${responsible.position_name})`
    }
  }

  if (appraisalType?.appraisal_type_code === 'APT01') {
    responsibleInput = (
      <Autocomplete
        disabled={adopted}
        value={selected}
        onChange={(e, value) => {
          inputChanged({ ...rowData, responsible_id: value?.value || null, responsible_filter: '' })
        }}
        onInputChange={(e, value, reason) => {
          if (reason === 'input') {
            responsibleSearchChanged(value, {
              ...rowData,
              responsible_filter: value,
              responsible: null,
              responsible_id: null
            })
          }
        }}
        loading={loading}
        size={'small'}
        options={loading ? [] : responsibleOptions}
        getOptionLabel={option => option.label || ''}
        renderInput={params => (
          <TextField
            disabled={adopted}
            style={{ width: '100%' }}
            variant={'outlined'}
            label={translate('responsible')}
            required
            {...params}
          />
        )}
      />
    )
  }
  return (
    <TableFooter>
      <TableRow>
        <TableCell colSpan={7}>
          <Grid container spacing={1} justify={'space-between'}>
            <Grid item xs sm md lg={6}>
              {responsibleInput}
            </Grid>
            <Grid item xs sm md lg={6}>
              <TextField
                style={{ width: '100%' }}
                label={translate('rejectReason')}
                variant={'outlined'}
                multiline
                size={'small'}
                value={rowData.remark || ''}
                onChange={e => inputChanged({ ...rowData, remark: e.target.value })}
              />
            </Grid>
          </Grid>
        </TableCell>
      </TableRow>
    </TableFooter>
  )
}
